import { useEffect, useCallback, useRef } from 'react';
import type { CardPositions } from './useNowcastingState';

// ============================================================================
// Types
// ============================================================================

export interface UseCardDraggingParams {
  mapContainerRef: React.RefObject<HTMLDivElement | null>;
  cardPositions: CardPositions;
  setCardPositions: React.Dispatch<React.SetStateAction<CardPositions>>;
  draggingCard: string | null;
  setDraggingCard: React.Dispatch<React.SetStateAction<string | null>>;
  dragOffset: { x: number; y: number };
  setDragOffset: React.Dispatch<React.SetStateAction<{ x: number; y: number }>>;
  isPiPVisible: boolean;
}

export interface UseCardDraggingReturn {
  handleCardMouseDown: (
    cardId: string,
    e: React.MouseEvent,
    currentPosition: { x: number; y: number } | null,
    elementRef?: HTMLElement | null
  ) => void;
}

// ============================================================================
// Hook
// ============================================================================

/**
 * Hook to manage draggable card behavior.
 * 
 * Responsibilities:
 * - Handle mouse down on drag handle to start dragging
 * - Track mouse movement to update card position
 * - Handle mouse up to stop dragging
 * - Manage global event listeners for drag operations
 * - Auto-position PiP card when first opened
 */
export function useCardDragging({
  mapContainerRef,
  cardPositions,
  setCardPositions,
  draggingCard,
  setDraggingCard,
  dragOffset,
  setDragOffset,
  isPiPVisible,
}: UseCardDraggingParams): UseCardDraggingReturn {
  
  // Store the positioned parent for the current drag operation
  const positionedParentRef = useRef<HTMLElement | null>(null);
  
  // Handle mouse down on card header to start dragging
  const handleCardMouseDown = useCallback((
    cardId: string,
    e: React.MouseEvent,
    currentPosition: { x: number; y: number } | null,
    elementRef?: HTMLElement | null
  ) => {
    // Only allow dragging from the header area
    if ((e.target as HTMLElement).closest('.drag-handle')) {
      e.preventDefault();

      if (!elementRef) return;

      // Use the card's offsetParent to get the actual positioned ancestor
      // offsetParent is the positioned ancestor that the element is positioned relative to
      const positionedParent = (elementRef.offsetParent || document.body) as HTMLElement;
      positionedParentRef.current = positionedParent;
      
      const parentRect = positionedParent.getBoundingClientRect();
      
      // Get the card's current rendered position BEFORE any state changes
      const cardRect = elementRef.getBoundingClientRect();
      
      // Convert mouse position to parent-relative coordinates
      const mouseX = e.clientX - parentRect.left;
      const mouseY = e.clientY - parentRect.top;
      
      // Convert card position to parent-relative coordinates
      const cardX = cardRect.left - parentRect.left;
      const cardY = cardRect.top - parentRect.top;
      
      // Calculate offset from where the mouse is relative to the card's top-left corner
      const offsetX = mouseX - cardX;
      const offsetY = mouseY - cardY;

      // Set position if not already set (switches from CSS to absolute positioning)
      if (!currentPosition) {
        setCardPositions(prev => ({
          ...prev,
          [cardId]: { x: cardX, y: cardY },
        }));
      }

      // Set drag state and offset - React will batch these updates
      // The offset is calculated from the card's current visual position,
      // so even if the card re-renders with absolute positioning, the offset
      // will keep it in the same visual location
      setDragOffset({
        x: offsetX,
        y: offsetY,
      });
      
      setDraggingCard(cardId);
    }
  }, [setCardPositions, setDragOffset, setDraggingCard]);

  // Handle mouse move during drag
  const handleCardMouseMove = useCallback((e: MouseEvent) => {
    if (!draggingCard || !positionedParentRef.current) return;

    // Use the stored positioned parent from when drag started
    const parentRect = positionedParentRef.current.getBoundingClientRect();
    
    // Convert mouse position to parent-relative coordinates
    const mouseX = e.clientX - parentRect.left;
    const mouseY = e.clientY - parentRect.top;

    // Calculate new position based on mouse and offset
    setCardPositions(prev => ({
      ...prev,
      [draggingCard]: {
        x: mouseX - dragOffset.x,
        y: mouseY - dragOffset.y,
      },
    }));
  }, [draggingCard, dragOffset, setCardPositions]);

  // Handle mouse up to stop dragging
  const handleCardMouseUp = useCallback(() => {
    positionedParentRef.current = null;
    setDraggingCard(null);
  }, [setDraggingCard]);

  // Add/remove global mouse event listeners for dragging
  useEffect(() => {
    if (draggingCard) {
      window.addEventListener('mousemove', handleCardMouseMove);
      window.addEventListener('mouseup', handleCardMouseUp);
      return () => {
        window.removeEventListener('mousemove', handleCardMouseMove);
        window.removeEventListener('mouseup', handleCardMouseUp);
      };
    }
  }, [draggingCard, handleCardMouseMove, handleCardMouseUp]);

  // Position PiP in top-right when first opened
  useEffect(() => {
    if (isPiPVisible && mapContainerRef.current && !cardPositions.pip) {
      const containerWidth = mapContainerRef.current.offsetWidth;
      const pipWidth = 400; // PiP width
      const rightOffset = 60; // Distance from right edge (leaves space for toolbar buttons)
      setCardPositions(prev => ({
        ...prev,
        pip: {
          x: containerWidth - pipWidth - rightOffset,
          y: 100,
        },
      }));
    }
  }, [isPiPVisible, cardPositions.pip, mapContainerRef, setCardPositions]);

  return {
    handleCardMouseDown,
  };
}
